/*
 * BHAKTA cart behaviour, shared by the drawer and the cart page.
 *
 * Every mutation goes to the AJAX cart API and asks for the cart sections back.
 * Any element on the page carrying data-bk-cart-sync is refreshed from that one
 * response, so the drawer and the cart page can never show different carts.
 *
 * Only [data-bk-cart-content] is replaced. The surrounding shell — the drawer's
 * panel and header, the page's heading — stays in the DOM, so the drawer never
 * blinks and never loses its open state.
 *
 * Attribute writes for the gift message deliberately skip the re-render:
 * replacing the markup mid-typing would throw focus out of the textarea.
 */

/*
 * Wrapped for the same reason the product script is: the drawer and the cart
 * page both ask for this file, so on /cart two tags with the same src run one
 * after the other. Re-reading a top-level class declaration is a SyntaxError,
 * and it takes the whole file with it. Inside a function the second pass costs
 * nothing, and the customElements guards below already refuse to register twice.
 */
(() => {
  class BhaktaCartBase extends HTMLElement {
    connectedCallback() {
      this.errorEl = this.querySelector('[data-bk-error]');
      this.messageTimer = null;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));

      this.syncWordCount();
    }

    onClick(event) {
      const change = event.target.closest('[data-bk-change]');
      if (change) {
        this.changeLine(change.dataset.line, change.dataset.quantity);
        return;
      }

      const add = event.target.closest('[data-bk-add]');
      if (add) this.addVariant(add.dataset.variant);
    }

    async onChange(event) {
      const protection = event.target.closest('[data-bk-protection]');
      if (protection) {
        /*
         * The browser has already flipped the box, so a failed request has to put
         * it back — nothing else would tell the shopper the charge was not
         * applied.
         */
        const wanted = protection.checked;
        const ok = wanted
          ? await this.addVariant(protection.dataset.variant)
          : await this.changeLine(protection.dataset.line, 0);

        if (!ok && document.contains(protection)) protection.checked = !wanted;
        return;
      }

      const gift = event.target.closest('[data-bk-gift]');
      if (!gift) return;

      const panel = this.querySelector('[data-bk-gift-panel]');
      if (panel) panel.hidden = !gift.checked;

      this.updateAttributes({ Gift: gift.checked ? 'yes' : '' }, true);
    }

    onInput(event) {
      const field = event.target.closest('[data-bk-gift-message]');
      if (!field) return;

      const limit = parseInt(this.dataset.wordLimit, 10) || 40;
      const words = field.value.trim().split(/\s+/).filter(Boolean);

      if (words.length > limit) field.value = `${words.slice(0, limit).join(' ')} `;

      this.syncWordCount();

      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => {
        this.updateAttributes({ 'Gift Message': field.value.trim() }, false);
      }, 700);
    }

    syncWordCount() {
      const field = this.querySelector('[data-bk-gift-message]');
      const output = this.querySelector('[data-bk-word-count]');
      if (!field || !output) return;

      output.textContent = field.value.trim().split(/\s+/).filter(Boolean).length;
    }

    /* Every cart view currently in the DOM, so one response refreshes them all. */
    get syncTargets() {
      return Array.from(document.querySelectorAll('[data-bk-cart-sync][data-section-id]'));
    }

    get sectionsParam() {
      return {
        sections: this.syncTargets.map((el) => el.dataset.sectionId).join(','),
        sections_url: window.location.pathname,
      };
    }

    changeLine(line, quantity) {
      const value = parseInt(line, 10);
      if (!value) return Promise.resolve(false);

      return this.post(this.dataset.changeUrl, {
        line: value,
        quantity: parseInt(quantity, 10),
        ...this.sectionsParam,
      });
    }

    addVariant(id, quantity = 1) {
      const value = parseInt(id, 10);
      if (!value) return Promise.resolve(false);

      return this.post(this.dataset.addUrl, {
        items: [{ id: value, quantity: Math.max(1, parseInt(quantity, 10) || 1) }],
        ...this.sectionsParam,
      });
    }

    updateAttributes(attributes, render) {
      const body = { attributes };
      if (render) Object.assign(body, this.sectionsParam);

      return this.post(this.dataset.updateUrl, body, render);
    }

    async post(url, body, render = true) {
      this.classList.add('is-busy');
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        const response = await fetch(`${url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (!response.ok) {
          this.showError(data.description || data.message);
          return false;
        }

        if (render && data.sections) this.render(data.sections);
        return true;
      } catch (error) {
        this.showError();
        return false;
      } finally {
        this.classList.remove('is-busy');
      }
    }

    render(sections) {
      let counted = false;

      this.syncTargets.forEach((target) => {
        const html = sections[target.dataset.sectionId];
        if (!html) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-bk-cart-content]');
        const current = target.querySelector('[data-bk-cart-content]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;

        /*
         * The returned markup is the source of truth for every counter on the
         * page, the one in the header section included.
         */
        const source = doc.querySelector('[data-bk-cart-count]');
        if (source && !counted) {
          counted = true;
          document.querySelectorAll('[data-bk-cart-count]').forEach((el) => {
            el.textContent = source.textContent.trim();
          });
        }
      });

      this.syncWordCount();

      /*
       * Removing the last bottle with protection still switched on would leave a
       * charge and nothing to protect, so that state marks itself and is cleared
       * on the next pass.
       */
      const orphan = document.querySelector('[data-bk-strip-protection]');
      if (orphan) this.changeLine(orphan.dataset.bkStripProtection, 0);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || 'Something went wrong. Please try again.';
      this.errorEl.hidden = false;
    }
  }

  /* The cart page needs the mutations and nothing else. */
  class BhaktaCartPage extends BhaktaCartBase {}

  class BhaktaCartDrawer extends BhaktaCartBase {
    connectedCallback() {
      super.connectedCallback();

      this.panel = this.querySelector('[data-bk-panel]');
      this.opener = null;

      this.addEventListener('keydown', (event) => this.onKeydown(event));

      /*
       * Escape has to work wherever focus happens to be — the shopper may have
       * clicked back into the page — so the key is watched on the document and
       * only acted on while the drawer is open.
       */
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
      });

      // Any control anywhere on the page can open the drawer.
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-bk-cart-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      });

      window.bhaktaCart = this;
    }

    onClick(event) {
      /*
       * Anything outside the panel is outside the drawer — the scrim covers the
       * whole viewport, so this is what closes it on a click beside the panel.
       */
      if (event.target.closest('[data-bk-close]') || !event.target.closest('[data-bk-panel]')) {
        event.preventDefault();
        this.close();
        return;
      }

      super.onClick(event);
    }

    open(trigger) {
      this.opener = trigger || document.activeElement;
      this.hidden = false;
      document.body.style.overflow = 'hidden';

      // A frame between unhiding and the state flip, or the slide never runs.
      requestAnimationFrame(() => {
        this.dataset.open = 'true';
        if (this.panel) this.panel.focus();
      });
    }

    close() {
      this.dataset.open = 'false';
      document.body.style.overflow = '';

      const done = () => {
        this.hidden = true;
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        done();
      } else {
        setTimeout(done, 350);
      }

      if (this.opener && document.contains(this.opener)) this.opener.focus();
    }

    /* Escape is handled on the document; this is the focus trap only. */
    onKeydown(event) {
      if (event.key !== 'Tab' || !this.panel) return;

      const focusable = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, summary, [tabindex]:not([tabindex="-1"])'
      );
      const visible = Array.from(focusable).filter((el) => el.offsetParent !== null);
      if (!visible.length) return;

      const first = visible[0];
      const last = visible[visible.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('bhakta-cart-drawer')) {
    customElements.define('bhakta-cart-drawer', BhaktaCartDrawer);
  }

  if (!customElements.get('bhakta-cart-page')) {
    customElements.define('bhakta-cart-page', BhaktaCartPage);
  }
})();

/* Refresh the custom cart drawer after EasyGift cart changes. */
(() => {
  const drawer = document.querySelector('bhakta-cart-drawer');
  if (!drawer || drawer.dataset.egSyncInstalled === 'true') return;
  drawer.dataset.egSyncInstalled = 'true';
  let timer;
  let sequence = 0;
  let popupWasVisible = false;
  const refresh = () => {
    clearTimeout(timer);
    const request = ++sequence;
    timer = setTimeout(async () => {
      const ids = [...new Set(drawer.syncTargets.map(target => target.dataset.sectionId))];
      if (!ids.length) return;
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!response.ok) return;
        const sections = await response.json();
        if (request === sequence && drawer.isConnected) drawer.render(sections);
      } catch {}
    }, 150);
  };
  const observePopup = () => {
    const popup = document.getElementById('aca-modal-container');
    const visible = Boolean(popup && popup.getClientRects().length);
    if (popupWasVisible && !visible) refresh();
    popupWasVisible = visible;
  };
  new MutationObserver(observePopup).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
  });
  observePopup();
  let attempts = 0;
  const subscribe = () => {
    const events = window.EasyGift && window.EasyGift.Events;
    if (events && typeof events.subscribe === 'function') {
      events.subscribe('afterCartRequest', data => {
        if (data && data.payload && data.payload.attributes) return;
        refresh();
      });
      return;
    }
    if (++attempts < 60) setTimeout(subscribe, 500);
  };
  subscribe();
})();
